import { useState } from 'react';
import { Icons } from '@/utils/Icons';
import { useLazyGetMatchesQuery, useLazyGetSportCompetitionsQuery } from '@/store/api/sportsApi';
import { useGetHomeCasinoSidebarGamesQuery } from '@/store/api/casinoApi';
import { useCasinoGameLauncher } from '@/hooks/useCasinoGameLauncher';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SportIcon from '@/components/common/SportIcon';
import { SPORTS, SportItem } from '@/config/sports';
import { useInplayCounts } from '@/hooks/useInplayCounts';
import BrandLogo from '@/components/common/BrandLogo';
import ThemeToggle from '@/components/common/ThemeToggle';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import { useTranslation } from '@/i18n';

/* ================= TYPES ================= */

type ChildNode = {
  id: string;
  label: string;
};

type SidebarProps = {
  isOpen: boolean;
  onToggle: () => void;
};

/* ================= STATIC DATA ================= */

const rowBaseClass = 'flex w-full items-center rounded-lg text-left text-sm transition';
const rowClass = `${rowBaseClass} gap-3 px-3 py-2`;
const rowStateClass = (active: boolean, disabled = false) =>
  disabled
    ? 'cursor-not-allowed text-shell-muted/50'
    : active
      ? 'bg-shell-raised text-shell-fg'
      : 'text-shell-fg/85 hover:bg-shell-raised hover:text-shell-fg';

/* ================= SMALL PIECES ================= */

const SectionTitle = ({ title, action }: { title: string; action?: React.ReactNode }) => (
  <div className="flex items-center justify-between px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-shell-muted">
    {title}
    {action}
  </div>
);

const CountBadge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="ml-auto min-w-[24px] rounded-full bg-gold px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-gold-foreground">
      {count}
    </span>
  ) : null;

/* ================= COMPONENT ================= */

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t, tSport } = useTranslation();

  /* ---------- API ---------- */

  const { counts: inplayCounts, total: totalInplay } = useInplayCounts();
  const [getCompetitions] = useLazyGetSportCompetitionsQuery();
  const [getMatches] = useLazyGetMatchesQuery();
  const { data: casinoGames = [], isFetching: isFetchingCasinoGames } =
    useGetHomeCasinoSidebarGamesQuery({ section: 'OTHERS' });
  const { launchCasinoGame } = useCasinoGameLauncher();

  /* ---------- STATE ---------- */

  const [launchingId, setLaunchingId] = useState<number | null>(null);
  // Only one sport / competition expanded at a time
  const [expandedSport, setExpandedSport] = useState<string | null>(null);
  const [expandedCompetition, setExpandedCompetition] = useState<string | null>(null);
  const [sportCompetitions, setSportCompetitions] = useState<Record<string, ChildNode[]>>({});
  const [competitionMatches, setCompetitionMatches] = useState<Record<string, ChildNode[]>>({});

  /* ---------- DERIVED ---------- */

  const isInplayActive =
    pathname === '/home' && new URLSearchParams(search).get('view') === 'inplay';

  /* ---------- HANDLERS ---------- */

  const closeOnMobile = () => {
    if (isOpen) onToggle();
  };

  const goTo = (path: string) => {
    navigate(path);
    closeOnMobile();
  };

  const toggleSport = async (sport: SportItem) => {
    if (expandedSport === sport.apiName) {
      setExpandedSport(null);
      setExpandedCompetition(null);
      return;
    }
    setExpandedSport(sport.apiName);
    setExpandedCompetition(null);

    if (sportCompetitions[sport.apiName]) return;
    try {
      const res = await getCompetitions(sport.apiName).unwrap();
      setSportCompetitions((prev) => ({
        ...prev,
        [sport.apiName]:
          res?.competitions?.map((comp) => ({ id: String(comp.id), label: comp.name })) ?? [],
      }));
    } catch {
      toast.error('Failed to load competitions', { id: 'sidebar-competitions-error' });
    }
  };

  const toggleCompetition = async (sportName: string, competitionId: string) => {
    if (expandedCompetition === competitionId) {
      setExpandedCompetition(null);
      return;
    }
    setExpandedCompetition(competitionId);

    if (competitionMatches[competitionId]) return;
    try {
      const res = await getMatches({ sportName, seriesId: competitionId }).unwrap();
      setCompetitionMatches((prev) => ({
        ...prev,
        [competitionId]:
          res?.fixture?.map((match) => ({ id: String(match.id), label: match.name })) ?? [],
      }));
    } catch {
      toast.error('Failed to load matches', { id: 'sidebar-matches-error' });
    }
  };

  const handleCasinoGameClick = async (gameId: number) => {
    setLaunchingId(gameId);
    try {
      await launchCasinoGame(gameId, 'mac88');
      closeOnMobile();
    } catch {
      toast.error('Failed to launch game', { id: 'sidebar-game-launch-error' });
    } finally {
      setLaunchingId(null);
    }
  };

  /* ================= UI ================= */

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-secondary/60 xl:hidden" onClick={onToggle} />
      )}

      <aside
        className={`fixed left-0 top-0 z-50 h-full w-[280px] shrink-0 overflow-y-auto border-r border-shell-border bg-shell-panel pb-4 transition-transform duration-300
          xl:sticky xl:top-[84px] xl:z-auto xl:m-3 xl:h-auto xl:max-h-[calc(100vh-96px)] xl:w-[250px] xl:translate-x-0 xl:self-start xl:rounded-xl xl:border 2xl:w-[270px]
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Mobile drawer header */}
        <div className="flex items-center justify-between border-b border-shell-border px-4 py-3 xl:hidden">
          <BrandLogo className="h-8 w-auto object-contain" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              type="button"
              onClick={onToggle}
              aria-label="Close menu"
              className="text-shell-muted hover:text-shell-fg"
            >
              <Icons.close size={22} />
            </button>
          </div>
        </div>

        <div className="px-2">
          {/* ---------- SPORTS ---------- */}
          <SectionTitle title={t('sidebar.sports')} />

          <button
            type="button"
            onClick={() => goTo('/home?view=inplay')}
            className={`${rowClass} ${rowStateClass(isInplayActive)}`}
          >
            <span className="relative flex size-5 items-center justify-center">
              <span className="absolute size-3 animate-ping rounded-full bg-danger-red/60" />
              <span className="size-2.5 rounded-full bg-danger-red" />
            </span>
            {t('nav.inPlay')}
            <CountBadge count={totalInplay} />
          </button>

          {SPORTS.map((sport) => {
            const path = `/all-sports/${sport.apiName}`;
            const active = decodeURIComponent(pathname) === path;
            const expanded = expandedSport === sport.apiName;
            const competitions = sportCompetitions[sport.apiName];

            return (
              <div key={sport.apiName}>
                <div className={`${rowBaseClass} ${rowStateClass(active, sport.disabled)} pr-1`}>
                  <button
                    type="button"
                    disabled={sport.disabled}
                    onClick={() => goTo(path)}
                    className="flex min-w-0 flex-1 items-center gap-3 py-2 pl-3 text-left"
                  >
                    <SportIcon name={sport.label} />
                    <span className="truncate">{tSport(sport.label)}</span>
                    {sport.disabled ? (
                      <span className="ml-auto text-[10px] uppercase">{t('sidebar.soon')}</span>
                    ) : (
                      <CountBadge count={inplayCounts[sport.apiName.toLowerCase()] ?? 0} />
                    )}
                  </button>
                  {!sport.disabled && (
                    <button
                      type="button"
                      onClick={() => toggleSport(sport)}
                      aria-label={`${expanded ? 'Hide' : 'Show'} ${sport.label} competitions`}
                      className="flex size-7 shrink-0 items-center justify-center rounded-md text-shell-muted hover:bg-shell hover:text-shell-fg"
                    >
                      {expanded ? <Icons.Up size={10} /> : <Icons.Down size={10} />}
                    </button>
                  )}
                </div>

                {expanded && (
                  <div className="mb-1 ml-5 border-l border-shell-border pl-2">
                    {!competitions && (
                      <div className="px-2 py-1.5 text-xs text-shell-muted">
                        {t('common.loading')}
                      </div>
                    )}
                    {competitions?.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-shell-muted">
                        {t('sidebar.noCompetitions')}
                      </div>
                    )}
                    {competitions?.map((comp) => {
                      const compExpanded = expandedCompetition === comp.id;
                      const matches = competitionMatches[comp.id];
                      return (
                        <div key={comp.id}>
                          <button
                            type="button"
                            onClick={() => toggleCompetition(sport.apiName, comp.id)}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-shell-fg/80 hover:bg-shell-raised hover:text-shell-fg"
                          >
                            <span className="flex-1">{comp.label}</span>
                            {compExpanded ? <Icons.Up size={9} /> : <Icons.Down size={9} />}
                          </button>
                          {compExpanded && (
                            <div className="ml-2 border-l border-shell-border pl-2">
                              {!matches && (
                                <div className="px-2 py-1 text-xs text-shell-muted">
                                  {t('common.loading')}
                                </div>
                              )}
                              {matches?.length === 0 && (
                                <div className="px-2 py-1 text-xs text-shell-muted">
                                  {t('sidebar.noMatches')}
                                </div>
                              )}
                              {matches?.map((match) => (
                                <button
                                  key={match.id}
                                  type="button"
                                  onClick={() => goTo(`/game-details/${sport.apiName}/${match.id}`)}
                                  className="block w-full rounded-md px-2 py-1 text-left text-xs text-shell-muted hover:bg-shell-raised hover:text-gold"
                                >
                                  {match.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* ---------- CASINO ---------- */}
          <div className="mx-3 mt-3 border-t border-shell-border" />
          <SectionTitle
            title={t('sidebar.casino')}
            action={
              <Link
                to="/casino"
                onClick={closeOnMobile}
                className="normal-case tracking-normal text-gold hover:underline"
              >
                {t('common.viewAll')}
              </Link>
            }
          />

          {isFetchingCasinoGames && casinoGames.length === 0 && (
            <div className="px-3 py-2 text-xs text-shell-muted">{t('common.loading')}</div>
          )}

          {casinoGames.map((game) => {
            const isLaunching = launchingId === game.id;
            return (
              <button
                key={game.id}
                type="button"
                disabled={isLaunching}
                onClick={() => handleCasinoGameClick(game.id)}
                className={`${rowClass} ${rowStateClass(false)}`}
              >
                {game.gameImage ? (
                  <img
                    src={game.gameImage}
                    alt=""
                    className="size-6 shrink-0 rounded object-cover"
                  />
                ) : (
                  <Icons.Trophy className="size-4 shrink-0 text-shell-muted" />
                )}
                <span className="truncate">{game.name}</span>
                {isLaunching && (
                  <span className="ml-auto size-3 animate-spin rounded-full border-2 border-gold border-t-transparent" />
                )}
              </button>
            );
          })}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
